import React, { Component, useEffect, useRef, useState } from 'react';
import { isHebrew } from '../../../App';
import { makeStyles } from '@material-ui/core/styles';

import {
  Box,
  Grid,
  Typography,
  TextField,
  Input,
  CircularProgress,
  Button,
} from '@material-ui/core';
import { BodyText, HeadingText } from './TextHelpers';
import EmojiObjectsIcon from '@material-ui/icons/EmojiObjects';
import Paper from '@material-ui/core/Paper';
import { useTranslation } from 'react-i18next';
import { AirtableManager } from '../../../VLBase/Managers/VLAirtableManager';
import { analytics, AnalyticsEvent } from '../../../utils/analytics';

const airtable = new AirtableManager();

const useStyles = makeStyles((theme) => ({
  root: {
    background: '#0e091e',
    minHeight: '100vh',
  },
  hero: {
    minHeight: '70vh',
  },
  formBackground: {
    borderRadius: '4px',
    background: '#1C172B',
  },
  formRoot: {
    '& > *': {
      marginBottom: theme.spacing(3),
      marginTop: theme.spacing(3),
    },
  },
  underline: {
    '&:before': {
      borderBottom: '1px solid #C8CACF',
      height: '2px',
    },
    '&:after': {
      borderBottom: `2px solid ${theme.palette.primary.main}`,
      height: '2px',
    },
    '&:hover:not($disabled):not($focused):not($error):before': {
      borderBottom: `2px solid ${theme.palette.primary.main}`,
      height: '2px',
    },
    '&:hover:not($disabled):not($focused):not($error):after': {
      borderBottom: `2px solid ${theme.palette.primary.main}`,
      height: '2px',
    },
  },
  textField: {
    '& .MuiFormLabel-root': {
      color: 'white',
    },
    color: 'white',
  },
  textFieldRoot: {
    '& .MuiInputBase-input': {
      color: '#fff', // Text color
    },
    '& .MuiInput-underline:before': {
      borderBottomColor: '#fff8', // Semi-transparent underline
    },
    '& .MuiInput-underline:hover:before': {
      borderBottomColor: '#fff', // Solid underline on hover
    },
    '& .MuiInput-underline:after': {
      borderBottomColor: '#fff', // Solid underline on focus
    },
  },
  notchedOutline: {
    borderColor: 'rgba(255, 255, 255, 0.7)',
  },
  submitButton: {
    height: '48px',
  },
  formInfo: {
    width: '75%',
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
}));

const InputBox = (props) => {
  const classes = useStyles({ isHebrew: isHebrew() });
  return (
    <TextField
      required
      error={props.showError}
      variant="outlined"
      {...props}
      fullWidth={true}
      InputLabelProps={{
        style: { color: 'rgba(255, 255, 255, 0.7)' },
        shrink: true,
      }}
      InputProps={{
        classes: {
          input: classes.textField,
          root: classes.textFieldRoot,
          notchedOutline: classes.notchedOutline,
        },
      }}
    />
  );
};

const FormValues = {
  first_name: { label: 'First name', placeholder: 'John' },
  last_name: { label: 'Last name', placeholder: 'Smith' },
  company_name: { label: 'Company name', placeholder: 'AwesomeBiz' },
  email: { label: 'Email', placeholder: 'something@domain.com' },
  phone: { label: 'Phone', placeholder: 'Phone number' },
  message: { label: 'Message', placeholder: 'How can we help you today?' },
};

const FormValues_hebrew = {
  first_name: { label: 'שם פרטי', placeholder: 'ישראל' },
  last_name: { label: 'שם משפחה', placeholder: 'ישראלי' },
  company_name: { label: 'שם העסק', placeholder: 'פגזינו' },
  email: { label: 'מייל', placeholder: 'something@domain.com' },
  phone: { label: 'טלפון', placeholder: 'מספר טלפון' },
  message: {
    label: 'הודעה',
    placeholder: 'תנו לנו קצת רקע על איך נוכל לעזור לכם',
  },
};

const FormPart = (props) => {
  const classes = useStyles({ isHebrew: isHebrew() });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [firstNameError, setFirstNameError] = useState(false);
  const [lastNameError, setLastNameError] = useState(false);
  const [companyNameError, setCompanyNameError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [phoneError, setPhoneError] = useState(false);
  const [messageError, setMessageError] = useState(false);
  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);
  const companyNameRef = useRef(null);
  const emailRef = useRef(null);
  const phoneRef = useRef(null);
  const messageRef = useRef(null);

  const onSubmitClicked = () => {
    //grab all values

    const firstName = firstNameRef.current?.value;
    const lastName = lastNameRef.current?.value;
    const companyName = companyNameRef.current?.value;
    const email = emailRef.current?.value;
    const phone = phoneRef.current?.value;
    const message = messageRef.current?.value;

    const hasFirstNameError = !firstName || firstName.length === 0;
    const hasLastNameError = !lastName || lastName.length === 0;
    const hasCompanyNameError = !companyName || companyName.length === 0;
    const hasEmailError = !email || email.length === 0;
    const hasPhoneError = !phone || phone.length === 0;

    const hasAnyErrors =
      hasFirstNameError ||
      hasLastNameError ||
      hasCompanyNameError ||
      hasEmailError ||
      hasPhoneError;

    //update state errors
    setFirstNameError(hasFirstNameError);
    setLastNameError(hasLastNameError);
    setCompanyNameError(hasCompanyNameError);
    setEmailError(hasEmailError);
    setPhoneError(hasPhoneError);

    if (hasAnyErrors) {
      analytics.event(
        new AnalyticsEvent(
          'web - flow - business landing - attempt submit incomplete form'
        )
      );
      return;
    }

    setIsSubmitting(true);
    airtable.saveBusinessLead(
      firstName,
      lastName,
      companyName,
      email,
      phone,
      message,
      (success, error) => {
        analytics.event(
          new AnalyticsEvent('web - flow - business landing - submitted form')
        );
        setIsSubmitting(false);
        if (error) {
          //show
        } else {
          //clear all
          firstNameRef.current.value = '';
          lastNameRef.current.value = '';
          companyNameRef.current.value = '';
          emailRef.current.value = '';
          phoneRef.current.value = '';
          messageRef.current.value = '';
          setSubmitSuccess(true);
          setTimeout(() => {
            setSubmitSuccess(false);
          }, 5000);
        }
      }
    );
  };

  const heb = isHebrew();
  const { t, i18n } = useTranslation();
  const formValues = heb ? FormValues_hebrew : FormValues;

  return (
    <Box>
      <Box className={classes.formBackground} p={3}>
        <form noValidate autoComplete="off" className={classes.formRoot}>
          <Box display="flex" flexDirection="row">
            <Box mr={heb ? 0 : 1} ml={heb ? 1 : 0} width="100%">
              <InputBox
                showError={firstNameError}
                inputRef={firstNameRef}
                label={formValues.first_name.label}
                placeholder={formValues.first_name.placeholder}
              />
            </Box>
            <Box ml={heb ? 0 : 1} mr={heb ? 1 : 0} width="100%">
              <InputBox
                showError={lastNameError}
                inputRef={lastNameRef}
                label={formValues.last_name.label}
                placeholder={formValues.last_name.placeholder}
              />
            </Box>
          </Box>
          <Box width="100%">
            <InputBox
              showError={companyNameError}
              inputRef={companyNameRef}
              label={formValues.company_name.label}
              placeholder={formValues.company_name.placeholder}
            />
          </Box>
          <Box width="100%">
            <InputBox
              showError={emailError}
              inputRef={emailRef}
              label={formValues.email.label}
              placeholder={formValues.email.placeholder}
            />
          </Box>
          <Box width="100%">
            <InputBox
              showError={phoneError}
              inputRef={phoneRef}
              label={formValues.phone.label}
              placeholder={formValues.phone.placeholder}
            />
          </Box>
          <Box width="100%">
            <InputBox
              showError={messageError}
              inputRef={messageRef}
              label={formValues.message.label}
              placeholder={formValues.message.placeholder}
            />
          </Box>
        </form>
        <Box mt={3}>
          <Button
            onClick={() => onSubmitClicked()}
            style={{ textTransform: 'none' }}
            className={classes.submitButton}
            variant="contained"
            color="primary"
            fullWidth={true}
          >
            {isSubmitting ? (
              <CircularProgress size={20} style={{ color: 'white' }} />
            ) : submitSuccess ? (
              t('biz_lp_form_submit_success')
            ) : (
              t('title_submit')
            )}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export const FormSection = (props) => {
  const classes = useStyles({ isHebrew: isHebrew() });
  const { t, i18n } = useTranslation();
  const { scrollToRef } = props;
  return (
    <div>
      <Grid container spacing={2}>
        <Grid item xs="12" sm="12" md="6">
          <Box className={classes.formInfo}>
            <HeadingText fontSize="28px" lineHeight="32px">
              {t('biz_lp_form_info_title')}
            </HeadingText>
            <Box mt={2}>
              <BodyText fontSize="18px">
                {t('biz_lp_form_info_detail_1')}
              </BodyText>
              <BodyText fontSize="18px" mt={2} mb={3}>
                {t('biz_lp_form_info_detail_2')}
              </BodyText>
            </Box>
            <ul style={{ color: '#5d36bd', fontSize: '18px' }}>
              <li>
                <BodyText mb={1} fontSize="18px">
                  {t('biz_lp_form_info_bullet_1')}
                </BodyText>
              </li>
              <li>
                <BodyText mb={1} fontSize="18px">
                  {t('biz_lp_form_info_bullet_2')}
                </BodyText>
              </li>
              <li>
                <BodyText mb={1} fontSize="18px">
                  {t('biz_lp_form_info_bullet_3')}
                </BodyText>
              </li>
            </ul>
          </Box>
        </Grid>
        <Grid item xs="12" sm="12" md="6">
          <Box id="form-part">
            <div ref={scrollToRef}>
              <FormPart />
            </div>
          </Box>
        </Grid>
      </Grid>
    </div>
  );
};
